var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
import { createNetworkNode as createNetworkNode_ } from '@streamr/trackerless-network';
import { Logger, MetricsContext, StreamPartIDUtils } from '@streamr/utils';
import { EventEmitter } from 'eventemitter3';
import pull from 'lodash/pull';
import omit from 'lodash/omit';
import { Lifecycle, inject, scoped } from 'tsyringe';
import { Identity, IdentityInjectionToken } from './identity/Identity';
import { ConfigInjectionToken } from './ConfigTypes';
import { DestroySignal } from './DestroySignal';
import { OperatorRegistry } from './contracts/OperatorRegistry';
import { OperatorDiscoveryRequest, OperatorDiscoveryResponse } from './generated/packages/sdk/protos/SdkRpc';
import { OperatorDiscoveryClient } from './generated/packages/sdk/protos/SdkRpc.client';
import { StreamMessageTranslator } from './protocol/StreamMessageTranslator';
import { pOnce } from './utils/promises';
import { convertPeerDescriptorToNetworkPeerDescriptor, peerDescriptorTranslator } from './utils/utils';
/**
 * The factory is used so that integration tests can replace the real network node with a fake instance
 */
/* eslint-disable class-methods-use-this */
let NetworkNodeFactory = class NetworkNodeFactory {
    createNetworkNode(opts) {
        return createNetworkNode_(opts);
    }
};
NetworkNodeFactory = __decorate([
    scoped(Lifecycle.ContainerScoped)
], NetworkNodeFactory);
export { NetworkNodeFactory };
const logger = new Logger('NetworkNodeFacade');
/**
 * Wrap a network node.
 * Lazily creates & starts node on first call to getNode().
 */
let NetworkNodeFacade = class NetworkNodeFacade {
    cachedNode;
    startNodeCalled = false;
    startNodeComplete = false;
    messageListeners = [];
    networkNodeFactory;
    operatorRegistry;
    config;
    identity;
    eventEmitter;
    destroySignal;
    constructor(networkNodeFactory, operatorRegistry, config, identity, destroySignal) {
        this.networkNodeFactory = networkNodeFactory;
        this.operatorRegistry = operatorRegistry;
        this.config = config;
        this.identity = identity;
        this.eventEmitter = new EventEmitter();
        this.destroySignal = destroySignal;
        destroySignal.onDestroy.listen(this.destroy);
    }
    async getNetworkOptions() {
        const entryPoints = await this.getEntryPoints();
        const localPeerDescriptor = this.config.network.controlLayer.peerDescriptor ?
            peerDescriptorTranslator(this.config.network.controlLayer.peerDescriptor) : undefined;
        return {
            layer0: {
                ...this.config.network.controlLayer,
                entryPoints: entryPoints.map(peerDescriptorTranslator),
                peerDescriptor: localPeerDescriptor,
                websocketPortRange: (this.config.network.controlLayer.websocketPortRange !== null)
                    ? this.config.network.controlLayer.websocketPortRange
                    : undefined
            },
            networkNode: {
                ...omit(this.config.network.node, 'contentDeliveryBufferWhileConnecting'),
                bufferWhileConnecting: this.config.network.node.contentDeliveryBufferWhileConnecting
            },
            metricsContext: new MetricsContext()
        };
    }
    /**
     * Stop network node, or wait for it to stop if already stopping.
     * Subsequent calls to getNode/start will fail.
     */
    destroy = pOnce(async () => {
        const node = this.cachedNode;
        this.cachedNode = undefined;
        // stop node only if started or in progress
        if (node && this.startNodeCalled) {
            if (!this.startNodeComplete) {
                // wait for start to finish before stopping node
                const startNodeTask = this.startNodeTask();
                this.startNodeTask.reset(); // allow subsequent calls to fail
                await startNodeTask;
            }
            await node.stop();
        }
        this.startNodeTask.reset(); // allow subsequent calls to fail
    });
    /**
     * Start network node, or wait for it to start if already started.
     */
    // TODO: doJoin parameter seems problematic here; see ticket NET-1319
    startNodeTask = pOnce(async (doJoin = true) => {
        this.startNodeCalled = true;
        try {
            const node = await this.initNode();
            if (!this.destroySignal.isDestroyed()) {
                await node.start(doJoin);
            }
            node.addMessageListener((msg) => {
                if (this.messageListeners.length > 0) {
                    try {
                        const translated = StreamMessageTranslator.toClientProtocol(msg);
                        for (const listener of this.messageListeners) {
                            listener(translated);
                        }
                    }
                    catch (err) {
                        logger.trace(`Could not translate message`, { err });
                    }
                }
            });
            if (this.destroySignal.isDestroyed()) {
                await node.stop();
            }
            else {
                this.eventEmitter.emit('start');
            }
            this.destroySignal.assertNotDestroyed();
            return node;
        }
        finally {
            this.startNodeComplete = true;
        }
    });
    async initNode() {
        this.destroySignal.assertNotDestroyed();
        if (this.cachedNode) {
            return this.cachedNode;
        }
        const node = this.networkNodeFactory.createNetworkNode(await this.getNetworkOptions());
        if (!this.destroySignal.isDestroyed()) {
            this.cachedNode = node;
        }
        return node;
    }
    startNode = this.startNodeTask;
    getNode() {
        this.destroySignal.assertNotDestroyed();
        return this.startNodeTask();
    }
    async getNodeId() {
        const node = await this.getNode();
        return node.getNodeId();
    }
    async join(streamPartId, neighborRequirement, deliveryOptions) {
        const node = await this.getNode();
        await node.join(streamPartId, neighborRequirement, deliveryOptions);
    }
    async leave(streamPartId) {
        const node = await this.getNode();
        await node.leave(streamPartId);
    }
    async broadcast(msg, deliveryOptions) {
        const node = await this.getNode();
        node.broadcast(StreamMessageTranslator.toProtobuf(msg), deliveryOptions);
    }
    addMessageListener(listener) {
        this.messageListeners.push(listener);
    }
    removeMessageListener(listener) {
        pull(this.messageListeners, listener);
    }
    async isProxiedStreamPart(streamPartId) {
        const node = await this.getNode();
        return node.isProxiedStreamPart(streamPartId);
    }
    async getMetricsContext() {
        const node = await this.getNode();
        return node.getMetricsContext();
    }
    async getPeerDescriptor() {
        const node = await this.getNode();
        return node.getPeerDescriptor();
    }
    async getDiagnosticInfo() {
        const node = await this.getNode();
        return node.getDiagnosticInfo();
    }
    async getStreamParts() {
        const node = await this.getNode();
        return node.getStreamParts();
    }
    async getNeighbors(streamPartId) {
        const node = await this.getNode();
        return node.getNeighbors(streamPartId);
    }
    async getOptions() {
        const node = await this.getNode();
        return node.getOptions();
    }
    async inspect(node, streamPartId) {
        if (this.isStarting()) {
            await this.startNodeTask(false);
        }
        const peerDescriptor = peerDescriptorTranslator(node);
        return this.cachedNode.inspect(peerDescriptor, streamPartId);
    }
    async setProxies(streamPartId, nodes, direction, connectionCount) {
        if (this.isStarting()) {
            await this.startNodeTask(false);
        }
        const peerDescriptors = nodes.map(peerDescriptorTranslator);
        await this.cachedNode.setProxies(streamPartId, peerDescriptors, await this.identity.getUserId(), direction, connectionCount);
    }
    async setStreamPartEntryPoints(streamPartId, nodeDescriptors) {
        if (this.isStarting()) {
            await this.startNodeTask(false);
        }
        const peerDescriptors = nodeDescriptors.map(peerDescriptorTranslator);
        this.cachedNode.setStreamPartEntryPoints(streamPartId, peerDescriptors);
    }
    async discoverOperators(leader, streamPartId) {
        const client = await this.createExternalRpcClient(OperatorDiscoveryClient);
        const response = await client.discoverOperators(OperatorDiscoveryRequest.create({ streamPartId }), {
            sourceDescriptor: await this.getPeerDescriptor(),
            targetDescriptor: peerDescriptorTranslator(leader)
        });
        return response.operators.map((operator) => convertPeerDescriptorToNetworkPeerDescriptor(operator));
    }
    async createExternalRpcClient(clientClass) {
        if (this.isStarting()) {
            await this.startNodeTask(false);
        }
        return this.cachedNode.createExternalRpcClient(clientClass);
    }
    async registerOperator(opts) {
        const node = await this.getNode();
        node.registerExternalNetworkRpcMethod(OperatorDiscoveryRequest, OperatorDiscoveryResponse, 'discoverOperators', async (request) => {
            const streamPartId = StreamPartIDUtils.parse(request.streamPartId);
            const operators = opts.getAssignedNodesForStreamPart(streamPartId);
            return OperatorDiscoveryResponse.create({ operators: operators.map((operator) => peerDescriptorTranslator(operator)) });
        });
    }
    isStarting() {
        return !this.cachedNode || !this.startNodeComplete;
    }
    once(eventName, listener) {
        this.eventEmitter.once(eventName, listener);
    }
    async getEntryPoints() {
        const discoveryConfig = this.config.network.controlLayer.entryPointDiscovery;
        const discoveredEntryPoints = (discoveryConfig?.enabled)
            ? await this.operatorRegistry.findRandomNetworkEntrypoints(discoveryConfig.maxEntryPoints, discoveryConfig.maxQueryResults, discoveryConfig.maxHeartbeatAgeHours)
            : [];
        return [...this.config.network.controlLayer.entryPoints, ...discoveredEntryPoints];
    }
};
NetworkNodeFacade = __decorate([
    scoped(Lifecycle.ContainerScoped),
    __param(2, inject(ConfigInjectionToken)),
    __param(3, inject(IdentityInjectionToken)),
    __metadata("design:paramtypes", [NetworkNodeFactory,
        OperatorRegistry, Object, Identity,
        DestroySignal])
], NetworkNodeFacade);
export { NetworkNodeFacade };
//# sourceMappingURL=NetworkNodeFacade.js.map